import { useCallback, useEffect, useState } from 'react';

export type AsyncState<T> =
  { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; error: unknown };

/**
 * Runs `fn` whenever `key` changes (or `reload()` is called). Results are tagged with the key
 * they belong to, so a stale answer is never shown for a new key.
 */
export function useAsync<T>(
  key: string,
  fn: () => Promise<T>,
): AsyncState<T> & { reload: () => void } {
  const [nonce, setNonce] = useState(0);
  const [result, setResult] = useState<{ tag: string; state: AsyncState<T> } | null>(null);
  const tag = `${key}#${nonce}`;
  useEffect(() => {
    let alive = true;
    fn().then(
      (data) => alive && setResult({ tag, state: { status: 'ready', data } }),
      (error: unknown) => alive && setResult({ tag, state: { status: 'error', error } }),
    );
    return () => {
      alive = false;
    };
    // fn is intentionally not a dependency: callers pass inline closures keyed by `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const state: AsyncState<T> = result && result.tag === tag ? result.state : { status: 'loading' };
  return { ...state, reload };
}
